import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { ShoppingBag, Search } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "cigars", label: "Cigars" },
  { id: "accessories", label: "Accessories" },
  { id: "apparel", label: "Apparel" },
  { id: "gift-cards", label: "Gift Cards" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState(params.get("category") || "all");
  const [q, setQ] = useState("");
  const { add } = useCart();

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (cat === "all") next.delete("category"); else next.set("category", cat);
    setParams(next, { replace: true });
  }, [cat]); // eslint-disable-line

  const filtered = useMemo(() => {
    let arr = products;
    if (cat !== "all") arr = arr.filter((p) => p.category === cat);
    if (q) arr = arr.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || (p.description || "").toLowerCase().includes(q.toLowerCase()));
    return arr;
  }, [products, cat, q]);

  return (
    <div data-testid="shop-page">
      <section className="pt-40 pb-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-[1600px] mx-auto">
          <p className="eyebrow">The Shop</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight">
            Objects, <span className="italic" style={{ color: "var(--twr-bronze)" }}>well made.</span>
          </h1>
          <p className="text-[var(--twr-warm-white)]/70 mt-6 max-w-2xl">
            Cigars from the great houses. Accessories chosen for the long sit. Apparel by the cut, not the season.
          </p>
        </div>
      </section>

      <section className="section-tight border-y border-[var(--twr-line)] sticky top-[72px] z-30 bg-[#0A0A0A]/85 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id} onClick={() => setCat(c.id)}
                data-testid={`shop-cat-${c.id}`}
                className={`pill ${cat === c.id ? "active" : ""}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <label className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--twr-warm-white)]/40" />
            <input
              data-testid="shop-search"
              placeholder="Search the shop…" value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none pl-9 pr-4 py-2.5 text-sm transition-colors"
            />
          </label>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1600px] mx-auto">
          <p className="text-[var(--twr-warm-white)]/55 text-sm mb-8">{filtered.length} items</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <article key={p.id} data-testid={`product-card-${p.id}`} className="group product-card overflow-hidden flex flex-col">
                <Link to={`/shop/${p.id}`} className="block aspect-square overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <p className="eyebrow">{p.category.replace(/-/g, " ")}</p>
                  <Link to={`/shop/${p.id}`} className="font-serif text-xl mt-3 leading-snug hover:text-[var(--twr-bronze)] transition-colors">{p.name}</Link>
                  <p className="text-[var(--twr-warm-white)]/60 text-sm mt-3 line-clamp-2 flex-1">{p.description}</p>
                  <div className="flex items-center justify-between mt-6 gap-3">
                    <span className="font-serif text-xl text-[var(--twr-bronze)]">${p.price.toFixed(2)}</span>
                    <button
                      data-testid={`product-add-${p.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); add(p, 1); toast.success(`Added · ${p.name}`); }}
                      className="inline-flex items-center gap-2 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] text-[11px] tracking-[0.22em] uppercase px-4 py-2.5 transition-colors"
                    >
                      <ShoppingBag size={13} /> Add
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center py-20 text-[var(--twr-warm-white)]/55">Nothing here yet. Try another category.</p>
          )}
        </div>
      </section>
    </div>
  );
}
